import { SimpleNavSidebar } from "@/components/SimpleNavSidebar";
import { TutorialOverlay } from "@/components/TutorialOverlay";
import { TrialCountdown } from "@/components/subscription/TrialCountdown";
import { DevControls } from "@/components/dev/DevControls";
import { ChatSidebarGlobal } from "@/components/chat/ChatSidebarGlobal";
import { ChatSidebarToggle } from "@/components/chat/ChatSidebarToggle";
import { useChatSidebar } from "@/contexts/ChatSidebarContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";

interface AppLayoutProps {
  children: React.ReactNode;
  showSidebar?: boolean;
}

export const AppLayout = ({ children, showSidebar = true }: AppLayoutProps) => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const { isOpen: isChatSidebarOpen, toggleSidebar } = useChatSidebar();
  
  const [sidebarVisible, setSidebarVisible] = useState(!isMobile);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Default to collapsed (true) if no preference stored, matching SimpleNavSidebar
    const stored = localStorage.getItem("sidebar-collapsed");
    return stored !== null ? stored === "true" : true;
  });
  const [chatSidebarWidth, setChatSidebarWidth] = useState(400);
  
  // Update sidebar visibility when screen size changes
  useEffect(() => {
    setSidebarVisible(!isMobile);
  }, [isMobile]);
  
  // Listen for changes to the sidebar collapsed state
  useEffect(() => {
    const handleSidebarToggle = (event: CustomEvent<{isCollapsed: boolean}>) => {
      setIsCollapsed(event.detail.isCollapsed);
    };
    
    window.addEventListener("sidebar-toggle", handleSidebarToggle as EventListener);
    return () => window.removeEventListener("sidebar-toggle", handleSidebarToggle as EventListener);
  }, []);

  // Check if we're on the chat page
  const isOnChatPage = location.pathname === '/chat';
  
  // Calculate the main content margin based on chat sidebar state
  const getMainContentMargin = () => {
    if (isOnChatPage || isMobile || !isChatSidebarOpen) {
      return '';
    }
    return `mr-[${chatSidebarWidth}px]`;
  };

  return (
    <div className="flex min-h-screen w-full bg-background overflow-hidden">
      {showSidebar && sidebarVisible && <SimpleNavSidebar />}
      <div 
        className={cn(
          "flex-1 flex flex-col transition-all duration-300 ease-in-out relative z-10",
          showSidebar && !isMobile && (isCollapsed ? "md:ml-[56px]" : "md:ml-[220px]")
        )}
        style={{
          // Add right margin when chat sidebar is open (except on chat page and mobile)
          marginRight: !isOnChatPage && !isMobile && isChatSidebarOpen ? `${chatSidebarWidth}px` : '0px'
        }}
      >
        {/* Trial Countdown Banner */}
        <div className="px-4 py-1 border-b border-border bg-background z-10">
          <TrialCountdown />
        </div>
        
        <main className={cn(
          "flex-1 w-full overflow-x-hidden",
          isMobile && "pb-16"
        )}>
          {children}
        </main>
      </div>
      
      {/* Global Chat Sidebar - only render if not on chat page */}
      {!isOnChatPage && (
        <ChatSidebarGlobal 
          isOpen={isChatSidebarOpen}
          onToggle={toggleSidebar}
          onResize={setChatSidebarWidth}
        />
      )}
      
      {/* Chat Sidebar Toggle Button */}
      <ChatSidebarToggle />
      
      <TutorialOverlay />
      {/* Development mode controls for testing subscription states */}
      <DevControls />
    </div>
  );
};